import { useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { resetPassword } from '../../api/owner'
import {
  AuthError,
  AuthField,
  AuthFields,
  AuthHeading,
  AuthShell,
  AuthSubmit,
  authInputClass,
} from './AuthShell'

const minimumLength = 8

/**
 * Hoe stevig een wachtwoord is, in drie stappen.
 *
 * Bewust grof. Een precieze score suggereert dat wij weten hoe lang het kraken
 * duurt, en dat weten we niet; dit is een duwtje richting langer, meer niet.
 * Lengte weegt het zwaarst, want dat is wat echt telt.
 */
function strengthOf(password: string): { label: string; steps: number } {
  if (password.length < minimumLength) {
    return { label: 'Te kort', steps: 0 }
  }

  const variety =
    Number(/[a-z]/.test(password)) +
    Number(/[A-Z]/.test(password)) +
    Number(/[0-9]/.test(password)) +
    Number(/[^a-zA-Z0-9]/.test(password))

  if (password.length >= 16 || (password.length >= 12 && variety >= 3)) {
    return { label: 'Sterk', steps: 3 }
  }

  if (password.length >= 12 || variety >= 3) {
    return { label: 'Redelijk', steps: 2 }
  }

  return { label: 'Zwak', steps: 1 }
}

/** Ronde 21I — een nieuw wachtwoord kiezen met de link uit de mail. */
export function NewPasswordPage() {
  const { token = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const strength = strengthOf(password)
  const matches = repeat === '' || repeat === password
  const complete = password.length >= minimumLength && repeat === password

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)

    try {
      await resetPassword(token, password)
      // De sessiecookie staat er nu; OwnerGate moet opnieuw kijken wie we zijn.
      await queryClient.invalidateQueries({ queryKey: ['owner'] })
      navigate('/beheer')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Dat lukte niet.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthShell>
      <form onSubmit={(event) => void submit(event)} className="mt-9 lg:mt-0">
        <AuthHeading title="Kies een nieuw wachtwoord" />

        <AuthFields>
          <AuthField id="password" label="Nieuw wachtwoord">
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              autoFocus
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className={`${authInputClass} tracking-[0.2em]`}
            />

            {password !== '' && (
              <div className="mt-2.5 flex items-center gap-3">
                <span aria-hidden="true" className="flex flex-1 gap-1.5">
                  {[1, 2, 3].map((step) => (
                    <span
                      key={step}
                      className={
                        'h-1.5 flex-1 rounded-full ' +
                        (step <= strength.steps ? 'bg-brand-coral' : 'bg-brand-line')
                      }
                    />
                  ))}
                </span>
                <span className="text-brand-soft w-18 text-right text-[13px]">
                  {strength.label}
                </span>
              </div>
            )}
          </AuthField>

          <AuthField id="repeat" label="Nog een keer">
            <input
              id="repeat"
              type="password"
              autoComplete="new-password"
              value={repeat}
              onChange={(event) => setRepeat(event.target.value)}
              aria-invalid={!matches}
              className={`${authInputClass} tracking-[0.2em] ${matches ? '' : 'border-brand-coral'}`}
            />
            {!matches && (
              <p className="text-brand-coral mt-2 text-[13px]">
                Deze twee zijn nog niet hetzelfde.
              </p>
            )}
          </AuthField>

          <AuthSubmit busy={busy} disabled={!complete}>
            Opslaan en aanmelden
          </AuthSubmit>
        </AuthFields>

        {error && <AuthError>{error}</AuthError>}

        <p className="font-brand text-brand-faint mt-5 text-center text-[13px] leading-[1.5]">
          Minstens {minimumLength} tekens. Andere toestellen worden afgemeld.
        </p>
      </form>
    </AuthShell>
  )
}
